/**
 * TEMP-H-001: useExecutionSettings Hook
 * 
 * React Query hook for fetching execution settings and per-stage thresholds.
 * Provides single source of truth for launch settings.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { executionAPI } from '@/lib/execution-api';
import { executionKeys, type ExecutionSettings, APIClientError } from '@/lib/api-types';

interface UseExecutionSettingsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useExecutionSettings(
  executionId: string,
  options: UseExecutionSettingsOptions = {}
): UseQueryResult<ExecutionSettings, APIClientError> {
  const { enabled = true, refetchInterval } = options;

  return useQuery({
    queryKey: executionKeys.settings(executionId),
    queryFn: () => executionAPI.getSettings(executionId),
    enabled: enabled && !!executionId,
    refetchInterval,
    staleTime: 60 * 1000, // 1 minute - settings don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (execution not found) or 403 (unauthorized)
      if (error.status === 404 || error.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export default useExecutionSettings;